import type { InvoiceLine } from "@carsfinders/contracts";

import { t } from "../../../lib/i18n";
import {
  financeErrorCode,
  financeErrorMessage,
  type InvoiceErrorCode,
} from "../api";
import { cn, formatChf } from "../utils";

/**
 * The translated notice for a typed refusal of the car-invoice PREVIEW, which
 * answers the issue's own codes. A code without its own notice keeps the
 * server's message. Shared by the issue drawer and the bid page's «Оплата».
 */
const REFUSAL_NOTICE: Partial<Record<InvoiceErrorCode, string>> = {
  DEAL_NOT_FOUND: "adminFinance.drawer.refusal.dealNotFound",
  DEAL_NOT_PRICEABLE: "adminFinance.drawer.refusal.dealNotPriceable",
  PRICING_RULES_UNAVAILABLE:
    "adminFinance.drawer.refusal.pricingRulesUnavailable",
  DEAL_ALREADY_INVOICED: "adminFinance.drawer.refusal.dealAlreadyInvoiced",
  DEAL_NOT_INVOICEABLE: "adminFinance.drawer.refusal.dealNotInvoiceable",
};

export function refusalText(error: unknown, fallbackKey: string): string {
  const code = financeErrorCode(error);
  const notice = code ? REFUSAL_NOTICE[code] : undefined;
  return notice ? t(notice) : (financeErrorMessage(error) ?? t(fallbackKey));
}

/**
 * The left cell of one invoice line: the revenue dot, the caption, and under
 * it either how the value was arrived at or — on a LEGACY line an admin
 * corrected while car-line overrides briefly existed (2026-09-28) —
 * «розраховано: CHF X». Nothing writes such a line any more; stored invoices
 * are documents and keep it.
 */
export function InvoiceLineCaption({ line }: { line: InvoiceLine }) {
  const label = line.label ?? t(line.labelKey);
  const changed = typeof line.computedChf === "number";
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 text-[14px]">
        <span
          className={cn(
            "h-2 w-2 shrink-0 rounded-cf-xs",
            line.ours ? "bg-cf-brand" : "bg-cf-line",
          )}
          aria-hidden
        />
        <span className="truncate">{label}</span>
      </div>
      <div className="ml-4 font-mono text-[11px] text-cf-ink-subtle">
        {changed ? (
          <span>
            {t("adminFinance.drawer.lineComputed", {
              amount: formatChf(line.computedChf ?? 0),
            })}
          </span>
        ) : (
          t(line.basisKey, line.basisParams ?? undefined)
        )}
      </div>
    </div>
  );
}

/** Invoice lines as a read-only list: caption on the left, the billed CHF on the right. */
export function InvoiceLinesReadOnly({
  lines,
}: {
  lines: readonly InvoiceLine[];
}) {
  return (
    <div className="flex flex-col">
      {lines.map((line) => {
        const changed = typeof line.computedChf === "number";
        return (
          <div
            key={line.key}
            data-line={line.key}
            data-changed={changed ? "true" : undefined}
            className={cn(
              "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-cf-line-soft py-[7px]",
              changed && "bg-cf-warn-tint",
            )}
          >
            <InvoiceLineCaption line={line} />
            <span className="text-right font-mono text-[14px]">
              {formatChf(line.valueChf)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export interface InvoiceTotalRow {
  /** Stable hook for tests and styling, e.g. `total`, `prepayment`, `due`. */
  id: string;
  label: string;
  value: string;
  tone?: "info" | "success";
  /** The bottom line — larger, above a rule. */
  strong?: boolean;
}

/** The totals under a read-only invoice: Разом, передоплата, … До сплати. */
export function InvoiceTotalsReadOnly({
  rows,
}: {
  rows: readonly InvoiceTotalRow[];
}) {
  return (
    <div className="mt-2 flex flex-col gap-1">
      {rows.map((row) => (
        <div
          key={row.id}
          data-total-row={row.id}
          className={cn(
            "flex items-baseline justify-between gap-3",
            row.strong
              ? "mt-0.5 border-t border-cf-line-soft pt-1.5"
              : cn(
                  "text-[13px]",
                  row.tone === "info"
                    ? "text-cf-info-ink"
                    : row.tone === "success"
                      ? "text-cf-success"
                      : "text-cf-ink-muted",
                ),
          )}
        >
          <span className={cn(row.strong && "text-[15px] font-semibold")}>
            {row.label}
          </span>
          <span
            className={cn("font-mono", row.strong && "text-[22px] font-medium")}
          >
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}
